window.addEventListener("custom-api:authenticated", () => {
  if (!window.accountInit) {
    window.accountInit = true;

    const { ref, onMounted, computed } = Vue;
    const accountApp = Vue.createApp({
      name: "Account",
      setup() {
        const customer = ref(null);
        const company = ref(null);
        const activeFilter = ref("Toutes");
        const searchQuery = ref("");
        const loadingOrderId = ref(null);
        const currentOrder = ref(null);
        let produitsHabituels = ref(null);

        const {
          showCalendar,
          currentMonth,
          currentYear,
          monthNames,
          dayNames,
          daysInMonth,
          selectedStart,
          selectedEnd,
          toggleCalendar,
          prevMonth,
          nextMonth,
          selectDate,
          hoverDate,
          isSelected,
          isBetween,
          isHovering,
        } = useCalendar();

        const profileGrab = async () => {
          const res = await window.Shopify.custom.api.account.getProfile();
          const json = await res.json();
          customer.value = json;
        };

        const companyGrab = async () => {
          const res = await window.Shopify.custom.api.account.getCompany();
          const json = await res.json();

          if (json && json.orders && Array.isArray(json.orders)) {
            json.orders = json.orders.map(order => {
              const lineItems = order.lineItems || [];

              const enrichedLineItems = lineItems.map(item => {
                const discounts = item.discounts || [];

                const totalDiscount = discounts.reduce((sum, d) => {
                  const amt = d && d.amount != null ? Number(d.amount) : 0;
                  return sum + amt;
                }, 0);

                return {
                  ...item,
                  totalDiscount,
                };
              });

              const totalQuantity = lineItems.reduce((sum, item) => {
                const qty = Number(item.quantity) || 0;
                return sum + qty;
              }, 0);
              return {
                ...order,
                lineItems: enrichedLineItems,
                itemCount: totalQuantity,
              };
            });
          }

          company.value = json;

          getCurrentOrder();
        };

        const getCurrentOrder = () => {
          const order = new URL(window.location).searchParams.get("order_id");
          if (!order) return;

          const orders = company.value.orders || [];
          orders.forEach(o => {
            if (order == o.name) currentOrder.value = o;
          });
        };

        const reorder = async order => {
          if (!order || !order.lineItems || !order.lineItems.length) return;

          loadingOrderId.value = order.id;

          const failedItems = [];
          const items = [];

          try {
            for (const item of order.lineItems) {
              if (item.total > 0) {
                if (!item.variantId) {
                  failedItems.push(item.title || "Produit inconnu (ID manquant)");
                  continue;
                }

                const variantId = item.variantId.split("/")[4];

                items.push({
                  id: variantId,
                  quantity: item.quantity || 1,
                });
              }
            }

            if (items.length > 0) {
              const res = await fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ items }),
              });

              if (!res.ok) {
                if (res.status == 422)
                  showNotif(
                    "Impossible de renouveler cette commande. Un article de votre commande précédente n'est plus disponible dans la quantité demandée.",
                    "error"
                  );
                else showNotif("Une erreur est survenue lors de l’ajout des articles au panier.", "error");
              } else {
                showNotif("Tous les produits ont bien été ajoutés au panier", "success");
              }
            } else {
              showNotif("Aucun article valide à ajouter au panier.", "warning");
            }
          } catch (err) {
            console.error(err);
            showNotif("Une erreur est survenue lors de la commande", "error");
          } finally {
            loadingOrderId.value = null;
          }
        };

        const updateMarketingConsent = async () => {
          const toggle = document.querySelector(".consent .toggle");
          const state = !toggle.classList.contains("active");

          try {
            const res = await Shopify.custom.api.account.updateMarketingConsent({ emailConsent: state });
            if (!res.ok) {
              showNotif("Une erreur est survenue, merci de réessayer plus tard.", "error");
              throw new Error(await res.text());
            }

            showNotif("Vos préférences ont bien été enregistrés.", "success");

            if (state) toggle.classList.add("active");
            else toggle.classList.remove("active");
          } catch (err) {
            console.error(err);
            showNotif("Une erreur est survenue, merci de réessayer plus tard.", "error");
          }
        };

        const dlInvoices = async invoices => {
          if (typeof invoices === "string") {
            const id = invoices;
            try {
              const res = await window.Shopify.custom.api.invoice.getPdf(id);
              const clone = res.clone();

              if (!res.ok) {
                showNotif(`Erreur HTTP lors du téléchargement de la facture ${id}`, "error");
                const errText = await clone.text().catch(() => "");
                throw new Error(`HTTP ${res.status} ${res.statusText} pour ${id} — ${errText.slice(0, 300)}`);
              }

              const ct = (res.headers.get("Content-Type") || "").toLowerCase();
              if (!ct.includes("application/pdf")) {
                showNotif(`Erreur PDF lors du téléchargement de la facture ${id}`, "error");
                const errText = await clone.text().catch(() => "");
                throw new Error(`Réponse non-PDF (${ct}) pour ${id} — ${errText.slice(0, 300)}`);
              }

              const blob = await res.blob();
              if (!blob || blob.size === 0) {
                showNotif(`Erreur de taille de fichier lors du téléchargement de la facture ${id}`, "error");
                throw new Error(`PDF vide (0 octet) pour ${id}`);
              }

              const name = `${id}.pdf`;

              triggerDl(blob, name);
            } catch (err) {
              console.error(`[FACTURE ${id}] Échec de téléchargement :`, err);
            }
          } else {
            for (let i = 0; i < invoices.length; i++) {
              const id = invoices[i];
              try {
                const res = await window.Shopify.custom.api.invoice.getPdf(id);
                const clone = res.clone();

                if (!res.ok) {
                  showNotif(`Erreur HTTP lors du téléchargement de la facture ${id}`, "error");
                  const errText = await clone.text().catch(() => "");
                  throw new Error(`HTTP ${res.status} ${res.statusText} pour ${id} — ${errText.slice(0, 300)}`);
                }

                const ct = (res.headers.get("Content-Type") || "").toLowerCase();
                if (!ct.includes("application/pdf")) {
                  showNotif(`Erreur PDF lors du téléchargement de la facture ${id}`, "error");
                  const errText = await clone.text().catch(() => "");
                  throw new Error(`Réponse non-PDF (${ct}) pour ${id} — ${errText.slice(0, 300)}`);
                }

                const blob = await res.blob();
                if (!blob || blob.size === 0) {
                  showNotif(`Erreur de taille de fichier lors du téléchargement de la facture ${id}`, "error");
                  throw new Error(`PDF vide (0 octet) pour ${id}`);
                }

                const name = `${id}.pdf`;

                triggerDl(blob, name);
              } catch (err) {
                console.error(`[FACTURE ${id}] Échec de téléchargement :`, err);
              }

              await wait(1000);
            }
          }
        };

        const triggerDl = (blob, name) => {
          const url = URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = name;
          document.body.appendChild(a);
          a.click();
          a.remove();
        };

        function wait(ms) {
          return new Promise(r => setTimeout(r, ms));
        }

        const orderCounts = computed(() => {
          const orders = company.value.orders;

          return {
            toutes: orders.length,
            attente: orders.filter(o => o.fulfillmentStatus === "UNFULFILLED" && !o.cancelledAt).length,
            cours: orders.filter(o => o.fulfillmentStatus === "FULFILLED" && !o.cancelledAt).length,
            annulees: orders.filter(o => o.cancelledAt).length,
            terminees: 0,
          };
        });

        const filteredOrders = computed(() => {
          if (!company.value || !company.value.orders) return [];

          return company.value.orders.filter(order => {
            // #1 - Filter by ative status

            let matchStatus = true;

            switch (activeFilter.value) {
              case "En attente":
                matchStatus = order.fulfillmentStatus === "UNFULFILLED" && !order.cancelledAt;
                break;
              case "En cours":
                matchStatus = order.fulfillmentStatus === "FULFILLED" && !order.cancelledAt;
                break;
              case "Annulées":
                matchStatus = order.cancelledAt;
                break;
              case "Terminées":
                matchStatus = false;
                break;
            }

            // #2 - Filter by search query

            const query = searchQuery.value.trim().toLowerCase();
            const matchSearch = !query || (order.name && order.name.toLowerCase().includes(query));

            // #3 - Filter by date

            let matchDate = true;
            if (selectedStart.value && selectedEnd.value) {
              const createdAt = new Date(order.createdAt);
              const start = new Date(selectedStart.value);
              const end = new Date(selectedEnd.value);
              start.setHours(0, 0, 0, 0);
              end.setHours(23, 59, 59, 999);

              matchDate = createdAt >= start && createdAt <= end;
            }

            // #4 - Return filtered result
            return matchStatus && matchSearch && matchDate;
          });
        });

        const invoices = computed(() => {
          if (!company || !company.value.orders) return [];

          const today = new Date();
          const result = [];
          let state;

          company.value.orders.forEach(order => {
            if (!order.invoices) return;

            if (order.financialStatus == "PAID") state = "REGLE";
            else if (!order.dueDate) state = "NON-ECHU";
            else if (new Date(order.dueDate) < today) state = "ECHU";
            else state = "NON-ECHU";

            order.invoices.forEach(inv => {
              if (inv)
                result.push({ invoiceNumber: inv, total: order.total, date: order.dueDateDisplay, state: state });
            });
          });
          return result;
        });

        const invoicesCount = computed(() => {
          let totalEchu = 0;
          let totalNonEchu = 0;
          invoices.value.forEach(inv => {
            if (inv.state == "ECHU") totalEchu += inv.total;
            else if (inv.state == "NON-ECHU") totalNonEchu += inv.total;
          });
          return {
            tous: invoices.value.length,
            echus: invoices.value.filter(i => i.state === "ECHU").length,
            nonEchus: invoices.value.filter(i => i.state === "NON-ECHU").length,
            regles: invoices.value.filter(i => i.state === "REGLE").length,
            totalEchu: totalEchu,
            totalNonEchu: totalNonEchu,
          };
        });

        const filteredInvoices = computed(() => {
          if (!invoices.value) return [];

          return invoices.value.filter(inv => {
            let matchStatus = true;

            switch (activeFilter.value) {
              case "Échus":
                matchStatus = inv.state === "ECHU";
                break;
              case "Non échus":
                matchStatus = inv.state === "NON-ECHU";
                break;
              case "Réglés":
                matchStatus = inv.state === "REGLE";
                break;
            }

            return matchStatus;
          });
        });

        produitsHabituels = computed(() => {
          if (!company.value || !company.value.orders) return [];

          const allLineItems = company.value.orders.flatMap(order => order.lineItems || []);

          const productMap = new Map();
          allLineItems.forEach(item => {
            const key = item.title || "Produit inconnu";
            const existing = productMap.get(key) || {
              handle: item.product.handle,
              quantity: 0,
            };

            existing.quantity += Number(item.quantity) || 0;

            productMap.set(key, existing);
          });

          const sortedProducts = Array.from(productMap.values()).sort((a, b) => b.quantity - a.quantity);

          return sortedProducts;
        });

        const setFilter = label => {
          activeFilter.value = label;
        };

        function useCalendar() {
          const showCalendar = Vue.ref(false);
          const currentMonth = Vue.ref(new Date().getMonth());
          const currentYear = Vue.ref(new Date().getFullYear());
          const selectedStart = Vue.ref(null);
          const selectedEnd = Vue.ref(null);
          const hoveredDate = Vue.ref(null);

          const monthNames = [
            "Janvier",
            "Février",
            "Mars",
            "Avril",
            "Mai",
            "Juin",
            "Juillet",
            "Août",
            "Septembre",
            "Octobre",
            "Novembre",
            "Décembre",
          ];
          const dayNames = ["L", "M", "M", "J", "V", "S", "D"];

          const toggleCalendar = () => {
            showCalendar.value = !showCalendar.value;
          };

          const daysInMonth = Vue.computed(() => {
            const days = [];
            const date = new Date(currentYear.value, currentMonth.value, 1);
            while (date.getMonth() === currentMonth.value) {
              days.push({
                day: date.getDate(),
                date: new Date(date),
              });
              date.setDate(date.getDate() + 1);
            }
            return days;
          });

          const prevMonth = () => {
            if (currentMonth.value === 0) {
              currentMonth.value = 11;
              currentYear.value--;
            } else {
              currentMonth.value--;
            }
          };

          const nextMonth = () => {
            if (currentMonth.value === 11) {
              currentMonth.value = 0;
              currentYear.value++;
            } else {
              currentMonth.value++;
            }
          };

          const selectDate = date => {
            if (!selectedStart.value || (selectedStart.value && selectedEnd.value)) {
              selectedStart.value = date;
              selectedEnd.value = null;
            } else if (date < selectedStart.value) {
              selectedEnd.value = selectedStart.value;
              selectedStart.value = date;
            } else {
              selectedEnd.value = date;
            }
          };

          const hoverDate = date => {
            hoveredDate.value = date;
          };

          const isSelected = date => {
            return (
              (selectedStart.value && date.toDateString() === selectedStart.value.toDateString()) ||
              (selectedEnd.value && date.toDateString() === selectedEnd.value.toDateString())
            );
          };

          const isBetween = date => {
            if (!selectedStart.value || !selectedEnd.value) return false;
            return date > selectedStart.value && date < selectedEnd.value;
          };

          const isHovering = date => {
            if (!selectedStart.value || selectedEnd.value || !hoveredDate.value) return false;
            return (
              (date > selectedStart.value && date < hoveredDate.value) ||
              (date < selectedStart.value && date > hoveredDate.value)
            );
          };

          return {
            showCalendar,
            currentMonth,
            currentYear,
            monthNames,
            dayNames,
            daysInMonth,
            selectedStart,
            selectedEnd,
            hoveredDate,

            toggleCalendar,
            prevMonth,
            nextMonth,
            selectDate,
            hoverDate,
            isSelected,
            isBetween,
            isHovering,
          };
        }

        onMounted(() => {
          profileGrab();
          companyGrab();
        });

        return {
          customer,
          company,
          activeFilter,
          searchQuery,
          filteredOrders,
          invoices,
          invoicesCount,
          filteredInvoices,
          produitsHabituels,
          setFilter,
          orderCounts,
          reorder,
          updateMarketingConsent,
          dlInvoices,
          loadingOrderId,
          currentOrder,

          // Calendar
          showCalendar,
          toggleCalendar,
          currentMonth,
          currentYear,
          monthNames,
          dayNames,
          daysInMonth,
          prevMonth,
          nextMonth,
          selectDate,
          hoverDate,
          isSelected,
          isBetween,
          isHovering,
          selectedStart,
          selectedEnd,
        };
      },
    });
    accountApp.mount("#account-vue-app");
  }
});
